import { useTranslation } from 'react-i18next';
import { faPlus, faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect } from 'react';
import DeleteModal from '@shared/components/DeleteModal';
import PensionHistoryForm from './PensionHistoryForm';
import PreviousPage from '@shared/components/PreviousPage';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { personService } from '@shared/services/personService';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';


export default function PensionHistory() {
    const { t } = useTranslation();
    const { id } = useParams();
    const [pensionHistory, setPensionHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchPensionHistory = async () => {
            try {
                const response = await personService.getPensionHistory(id);
                setPensionHistory(response);
            } catch (error) {
                setError(error);
            }
        };
        fetchPensionHistory();
    }, [id]);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [sortBy, setSortBy] = useState('fromDate');
    const [sortDir, setSortDir] = useState('desc');
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [modalType, setModalType] = useState('add'); // 'add' or 'edit'

    const { register, handleSubmit, errors, setValue, watch, clearErrors } = useForm({
        resolver: yupResolver(yup.object({
            fromDate: yup.mixed()
                .test('is-required', t('validation.person.pensionHistory.fromDateRequired'), function (value) {
                    return value !== '' && value !== null && value !== undefined;
                })
                .test('not-future-date', t('validation.person.pensionHistory.fromDateNotFuture'), function (value) {
                    if (!value) return true;
                    const fromDate = new Date(value);
                    const today = new Date();
                    return fromDate <= today;
                })
                .nullable(),
            amount: yup.string()
                .required(t('validation.person.pensionHistory.amountRequired')),
            docId: yup.string()
                .nullable()
        }))
    });

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortDir('asc');
        }
    };

    const handleSelectRecord = (record) => {
        setSelectedRecord(record);
    };

    const handleShowDeleteModal = () => {
        if (selectedRecord) {
            setShowDeleteModal(true);
        }
    };

    const handleOpenFormModal = (type) => {
        setModalType(type);
        if (type === 'edit' && selectedRecord) {
            setValue('fromDate', selectedRecord.fromDate);
            setValue('sum', selectedRecord.sum);
            setValue('documentAttached', selectedRecord.documentNo);
        }
        setShowFormModal(true);
    };

    const handleCloseFormModal = () => {
        setShowFormModal(false);
    };

    const handleSaveRecord = async (data) => {
        try {
            if (modalType === 'edit') {
                await personService.updatePensionHistory(selectedRecord.id, data);
            } else {
                await personService.addPensionHistory(data);
            }
            const response = await personService.getPensionHistory(id);
            setPensionHistory(response);
            setShowFormModal(false);
        } catch (error) {
            console.error('Error saving record:', error);
        }
    };

    const handleDeleteRecord = async () => {
        try {
            await personService.deletePensionHistory(selectedRecord.id);
            const response = await personService.getPensionHistory(id);
            setPensionHistory(response);
            setShowDeleteModal(false);
        } catch (error) {
            console.error('Error deleting record:', error);
        }
    };

    return (
        <div className="pension-history-container flex-column width-70">
            <div className="table-header">
                <div className="title-section">
                    <h1 className="main-heading">{t('Compensations as per labor contracts.pensionHistory.title')}</h1>
                </div>
                <div className="controls-section">
                    <div className="header-icons">
                        <PreviousPage />

                        <button
                            className="icon-button"
                            onClick={() => handleOpenFormModal('add')}
                            title={t('common.add')}
                        >
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                        <button
                            className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                            onClick={() => selectedRecord && handleOpenFormModal('edit')}
                            title={t('common.edit')}
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                        <button
                            className={`icon-button ${!selectedRecord ? 'disabled-link' : ''}`}
                            onClick={handleShowDeleteModal}
                            disabled={!selectedRecord}
                            title={t('common.delete')}
                        >
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                </div>
            </div>
            <div className="control-table-container mb-4">
                <table className="table">
                    <tbody>
                        <tr>
                            <td>
                                <input type="text" className="form-control" value="00030" readOnly />
                            </td>
                            <td>
                                <input
                                    type="text"
                                    className="form-control"
                                    value={pensionHistory[0]?.person?.name || ''}
                                    readOnly
                                />
                            </td>
                        </tr>
                        <tr>
                            <td>За трудов договор:</td>
                            <td>
                                <select className="form-control">
                                    <option>Договор за управление и контрол - дог.№: 2020-1 / 01.02.2020  г.</option>
                                </select>
                            </td>
                        </tr>
                        <tr>
                            <td>Избор на:</td>
                            <td>
                                <select className="form-control">
                                    <option>Заплата</option>
                                    <option>Методи на изчисляване на работно време</option>
                                    <option>Продължителност на работен ден</option>
                                    <option selected>Размер на пенсия</option>
                                    <option>Намалена работоспособност</option>
                                    <option>Не се осигурява здравно</option>
                                    <option>Осигурен при друг/външен/ работодател</option>
                                    <option>Продължителност на работния ден при друг/външен/ работодател</option>
                                    <option>База за осигуряване от предх.работодател/при постъпване/</option>
                                </select>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <div className="table-responsive custom-table-container">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th className="radio-column"></th>
                            <th onClick={() => handleSort('fromDate')} className="sortable-header">
                                {t('Compensations as per labor contracts.pensionHistory.table.headers.fromDate')}
                                <span className="sort-indicator">
                                    {sortBy === 'fromDate' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                </span>
                            </th>
                            <th onClick={() => handleSort('toDate')} className="sortable-header">
                                {t('Compensations as per labor contracts.pensionHistory.table.headers.toDate')}
                                <span className="sort-indicator">
                                    {sortBy === 'toDate' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                </span>
                            </th>
                            <th onClick={() => handleSort('sum')} className="sortable-header">
                                {t('Compensations as per labor contracts.pensionHistory.table.headers.sum')}
                                <span className="sort-indicator">
                                    {sortBy === 'sum' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                </span>
                            </th>
                            <th onClick={() => handleSort('documentNo')} className="sortable-header">
                                {t('Compensations as per labor contracts.pensionHistory.table.headers.documentNo')}
                                <span className="sort-indicator">
                                    {sortBy === 'documentNo' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                </span>
                            </th>
                            <th onClick={() => handleSort('documentDescription')} className="sortable-header">
                                {t('Compensations as per labor contracts.pensionHistory.table.headers.documentDescription')}
                                <span className="sort-indicator">
                                    {sortBy === 'documentDescription' ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                                </span>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {pensionHistory.map((record) => (
                            <tr key={record.id}>
                                <td className="radio-column">
                                    <div className="checkbox-wrapper">
                                        <input
                                            type="checkbox"
                                            checked={selectedRecord?.id === record.id}
                                            onChange={() => handleSelectRecord(record)}
                                        />
                                    </div>
                                </td>
                                <td>{record.fromDate}</td>
                                <td>{record.toDate === "2200-10-10" ? t('Compensations as per labor contracts.pensionHistory.table.headers.tillNextChange') : record.toDate}</td>
                                <td>{record.amount}</td>
                                <td>{record.documentNo}</td>
                                <td>{record.documentDescription}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {showDeleteModal && selectedRecord && (
                <DeleteModal
                    onClose={() => setShowDeleteModal(false)}
                    onConfirm={handleDeleteRecord}
                    name={selectedRecord.documentNo}
                    entityType="record"
                />
            )}

            {showFormModal && (
                <PensionHistoryForm
                    onClose={handleCloseFormModal}
                    onConfirm={handleSaveRecord}
                    editData={modalType === 'edit' ? selectedRecord : null}
                    formProps={{ register, handleSubmit, setValue, watch, errors }}
                />
            )}
        </div>
    );
}
